import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, X } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@scio3/ui';
import {
  FREQUENCIES,
  RUN_WINDOWS,
  getTimezoneOptions,
} from '../../utils/report-constants';
import type { ReportFrequency } from '../../utils/report-types';
import { SendToField, isEntryComplete, type SendToEntry } from './SendToField';

/**
 * PROTOTYPE — "New broadcast" with the reworked Send-to section (/dev/broadcast-sendto).
 *
 * A copy of ScreenReportDialog with two things changed and nothing else: `ChannelSelector`
 * becomes `SendToField`, and Content is a fixture stand-in so the mock needs no Apollo, no
 * asset picker and no account. Field order, dialog width, scroll gutter, footer and submit
 * gating are the live ones, so a screenshot here is what the real dialog would look like.
 */

const TIMEZONE_OPTIONS = getTimezoneOptions();
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Content is out of scope for this mock — pre-picked so the field reads as filled. */
const FIXTURE_ITEMS = [
  { id: 'a1', name: 'Store Ops Dashboard' },
  { id: 'a2', name: 'Weekly KPI Board' },
];

interface ScreenReportDialogV2Props {
  open: boolean;
  onClose: () => void;
  /** Seed Send-to so a screenshot can show the settled-list state without clicking. */
  initialSendTo?: SendToEntry[];
  initialName?: string;
}

export function ScreenReportDialogV2({
  open,
  onClose,
  initialSendTo = [],
  initialName = '',
}: ScreenReportDialogV2Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [sendTo, setSendTo] = useState<SendToEntry[]>(initialSendTo);
  const [items, setItems] = useState(FIXTURE_ITEMS);
  const [frequency, setFrequency] = useState<ReportFrequency>('daily');
  const [runWindow, setRunWindow] = useState('9');
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);

  // Reset on each open, like the live dialog's hydrate/reset effect.
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setSendTo(initialSendTo);
    setItems(FIXTURE_ITEMS);
    setFrequency('daily');
    setRunWindow('9');
    setTimezone(DEFAULT_TIMEZONE);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fixture props are stable per story
  }, [open]);

  const sendToReady = sendTo.length > 0 && sendTo.every(isEntryComplete);
  const canCreate = !!name.trim() && items.length > 0 && sendToReady;
  const disabledTooltip = !name.trim()
    ? t('requiredError', { text: t('Broadcast name') })
    : sendTo.length === 0
      ? t('Add at least one destination')
      : !sendToReady
        ? t('Finish setting up every destination')
        : undefined;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="flex max-h-[85vh] max-w-lg flex-col"
        data-testid="screen-report-dialog-v2"
      >
        <DialogHeader>
          <DialogTitle>{t('New broadcast')}</DialogTitle>
        </DialogHeader>

        <div className="-mx-2 flex-1 space-y-4 overflow-y-auto px-2 py-1">
          {/* Name */}
          <div className="space-y-2">
            <Label>{t('Broadcast name')}</Label>
            <Input
              data-testid="report-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('Broadcast name')}
            />
          </div>

          {/* Send to — the reworked section */}
          <div className="space-y-2">
            <Label>{t('Send to')}</Label>
            <SendToField value={sendTo} onChange={setSendTo} />
          </div>

          {/* Content — fixture stand-in for ContentField */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('Content type')}</Label>
              <Select value="asset">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset">{t('Asset')}</SelectItem>
                  <SelectItem value="screen">{t('Screen')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('Selected assets')}</Label>
              <div className="flex gap-2">
                <Button
                  variant="trigger"
                  className={cn('min-w-0 flex-1 justify-start', items.length === 0 && 'text-muted-foreground')}
                >
                  <span className="truncate">
                    {items.length === 0
                      ? t('Select assets...')
                      : items.length === 1
                        ? items[0].name
                        : t('{{count}} assets', { count: items.length })}
                  </span>
                </Button>
                <Button variant="default" className="shrink-0">
                  {t('Change')}
                </Button>
              </div>
              {items.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {items.map((item) => (
                    <Badge key={item.id} variant="secondary" className="gap-1 pr-1 font-normal">
                      <ImageIcon className="h-3 w-3" />
                      {item.name}
                      <button
                        type="button"
                        aria-label={t('Remove')}
                        className="cursor-pointer rounded p-0.5 hover:bg-background/60"
                        onClick={() => setItems((prev) => prev.filter((i) => i.id !== item.id))}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label>{t('Frequency')}</Label>
            <Select value={frequency} onValueChange={(v) => setFrequency(v as ReportFrequency)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Run window */}
          <div className="space-y-2">
            <Label>{t('Run window')}</Label>
            <Select value={runWindow} onValueChange={setRunWindow}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {RUN_WINDOWS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Time zone */}
          <div className="space-y-2">
            <Label>{t('Time Zone')}</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {TIMEZONE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button disabled={!canCreate} disabledTooltip={disabledTooltip}>
            {t('Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
